'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, Zap, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useI18n } from './i18n-provider';
import GlobalSearch from './GlobalSearch';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const pathname = usePathname();
  const { t, isRtl } = useI18n();

  // 主要导航（始终显示）
  const mainNavLinks = [
    { href: '/', label: t.nav.home },
    { href: '/about', label: t.nav.about },
    { href: '/services', label: t.nav.services },
    { href: '/products', label: 'Products' },
    { href: '/success-stories', label: t.nav.successStories },
    { href: '/blog', label: t.nav.blog },
  ];

  // 资源下拉（合并 resources + wholesale + battery + faq 等）
  const resourceLinks = [
    { href: '/resources', label: t.nav.resources },
    { href: '/wholesale', label: t.nav.wholesale },
    { href: '/battery-solution', label: t.nav.batterySolution },
    { href: '/battery-diagnostic', label: 'Free Diagnostic' },
    { href: '/partners', label: 'Partners' },
    { href: '/faq', label: t.nav.faq },
  ];

  // 移动端全量导航
  const allNavLinks = [...mainNavLinks, ...resourceLinks];

  const isResourceActive = resourceLinks.some(
    (link) => link.href !== '/' && pathname?.startsWith(link.href),
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsResourcesOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[oklch(0.15_0.01_220)]/90 backdrop-blur-xl border-b border-[oklch(0.28_0.025_170)] shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group shrink-0">
            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-[oklch(0.62_0.19_150)] to-[oklch(0.85_0.12_140)] flex items-center justify-center shadow-lg shadow-[oklch(0.62_0.19_150)]/30 group-hover:shadow-[oklch(0.62_0.19_150)]/50 transition-shadow">
              <Zap className="w-5 h-5 text-[oklch(0.12_0.02_150)]" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-lg font-bold text-foreground group-hover:text-[oklch(0.72_0.22_150)] transition-colors">WFD</span>
              <span className="text-[10px] text-muted-foreground tracking-[0.15em] uppercase font-medium">
                NEW ENERGY
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {mainNavLinks.map((link) => {
              const isActive =
                pathname === link.href ||
                (link.href !== '/' && pathname?.startsWith(link.href));
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                    isActive
                      ? 'text-[oklch(0.72_0.22_150)]'
                      : 'text-muted-foreground hover:text-[oklch(0.72_0.22_150)]'
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-[oklch(0.62_0.19_150)] to-transparent" />
                  )}
                </Link>
              );
            })}

            {/* Resources Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsResourcesOpen(true)}
              onMouseLeave={() => setIsResourcesOpen(false)}
            >
              <button
                className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-1 ${
                  isResourceActive
                    ? 'text-[oklch(0.72_0.22_150)]'
                    : 'text-muted-foreground hover:text-[oklch(0.72_0.22_150)]'
                }`}
                onClick={() => setIsResourcesOpen(!isResourcesOpen)}
              >
                Resources
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
                {isResourceActive && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-[oklch(0.62_0.19_150)] to-transparent" />
                )}
              </button>

              {isResourcesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 min-w-[220px] py-2 rounded-xl bg-[oklch(0.20_0.015_220)]/95 backdrop-blur-xl border border-[oklch(0.28_0.025_170)] shadow-xl shadow-black/40 z-50 overflow-hidden">
                  {resourceLinks.map((link) => {
                    const isActive = pathname === link.href || pathname?.startsWith(link.href);
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          isActive
                            ? 'text-[oklch(0.72_0.22_150)] bg-[oklch(0.62_0.19_150)]/10'
                            : 'text-muted-foreground hover:text-foreground hover:bg-[oklch(0.26_0.02_220)]'
                        }`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <GlobalSearch />
            <LanguageSwitcher variant="minimal" />
            <Button asChild className="btn-glow px-5 h-10 rounded-xl text-sm">
              <Link href="/quote">{t.nav.getQuote}</Link>
            </Button>
          </div>

          {/* Mobile: Search + Menu */}
          <div className="flex xl:hidden items-center gap-1">
            <GlobalSearch />
            <button
              className="p-2 rounded-lg text-foreground hover:bg-[oklch(0.26_0.02_220)] transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="xl:hidden bg-[oklch(0.20_0.015_220)]/95 backdrop-blur-xl border-t border-[oklch(0.28_0.025_170)]">
          <nav className="max-w-7xl mx-auto px-6 py-4 space-y-1">
            {allNavLinks.map((link) => {
              const isActive =
                pathname === link.href ||
                (link.href !== '/' && pathname?.startsWith(link.href));
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`block px-4 py-3 text-base font-medium rounded-xl transition-all ${
                    isActive
                      ? 'text-[oklch(0.72_0.22_150)] bg-[oklch(0.62_0.19_150)]/10 border border-[oklch(0.62_0.19_150)]/30'
                      : 'text-muted-foreground hover:text-[oklch(0.72_0.22_150)] hover:bg-[oklch(0.26_0.02_220)]'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <div className="pt-4 flex items-center gap-3">
              <div className="flex-1">
                <LanguageSwitcher variant="minimal" />
              </div>
              <Button asChild className="flex-1 btn-glow h-12 rounded-xl">
                <Link href="/quote">{t.nav.getQuote}</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
